import { AppType } from "client/Api/Apps/common";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileData, TileRenderState } from "client/Features/Workspace/RenderState/common";
import { convertTile } from "client/Features/Workspace/RenderState/conversion";
import { useAddressBookTileNavigation } from "client/Features/Workspace/Tiles/AddressBook/AddressBookTileContent";
import { useAfasTileNavigation } from "client/Features/Workspace/Tiles/Afas/AfasTileContent";
import { useAutotaskTileNavigation } from "client/Features/Workspace/Tiles/Autotask/AutotaskTileContent";
import { useAzureAppTileNavigation } from "client/Features/Workspace/Tiles/AzureAppTileContent";
import { useCalendarTileNavigation } from "client/Features/Workspace/Tiles/Calendar/CalendarTileContent";
import { useCitrixXenAppTileNavigation } from "client/Features/Workspace/Tiles/CitrixXenAppTileContent";
import { useClientlessRdpTileNavigation } from "client/Features/Workspace/Tiles/ClientlessRdpTileContent";
import { useConnectWiseTileNavigation } from "client/Features/Workspace/Tiles/ConnectWise/ConnectWiseTileContent";
import { useDocumentsTileNavigation } from "client/Features/Workspace/Tiles/Documents/DocumentsTileContent";
import { useEmailTileNavigation } from "client/Features/Workspace/Tiles/Email/EmailTileContent";
import { useEmbedAppTileNavigation } from "client/Features/Workspace/Tiles/Embed/EmbedAppTileContent";
import { useGoogleTileNavigation } from "client/Features/Workspace/Tiles/Google/GoogleTileContent";
import { useHubTileNavigation } from "client/Features/Workspace/Tiles/Hub/HubTileContent";
import { useLiquitTileNavigation } from "client/Features/Workspace/Tiles/Liquit/LiquitTileContent";
import { useLocalAppTileNavigation } from "client/Features/Workspace/Tiles/LocalAppTileContent";
import { useMagisterTileNavigation } from "client/Features/Workspace/Tiles/Magister/MagisterTileContent";
import { useNedapOnsTileNavigation } from "client/Features/Workspace/Tiles/NedapOns/NedapOnsTileContent";
import { useOfficeTileNavigation } from "client/Features/Workspace/Tiles/OfficeTileContent";
import { usePeopleTileNavigation } from "client/Features/Workspace/Tiles/PeopleTileContent";
import { usePowerBiTileNavigation } from "client/Features/Workspace/Tiles/PowerBi/PowerBiTileContent";
import { useSharePointTileNavigation } from "client/Features/Workspace/Tiles/SharePoint/SharePointTileContent";
import { useShortcutTileNavigation } from "client/Features/Workspace/Tiles/ShortcutTileContent";
import { useSkypeTileNavigation } from "client/Features/Workspace/Tiles/SkypeTileContent";
import { useTeamsTileNavigation } from "client/Features/Workspace/Tiles/Teams/TeamsTileContent";
import { InteractionState } from "client/Features/Workspace/Tiles/TileContext";
import { useToDoTileNavigation } from "client/Features/Workspace/Tiles/ToDo/ToDoTileContent";
import { useTopDeskTileNavigation } from "client/Features/Workspace/Tiles/TopDesk/TopDeskTileContent";
import { useUnknownTileNavigation } from "client/Features/Workspace/Tiles/UnknownTileContent";
import { useVilansKickTileNavigation } from "client/Features/Workspace/Tiles/VilansKick/VilansKickTileContent";
import { useVmwareHorizonAppTileNavigation } from "client/Features/Workspace/Tiles/VmwareHorizonAppTileContent";
import { useWindowsVirtualDesktopTileNavigation } from "client/Features/Workspace/Tiles/WindowsVirtualDesktopAppTileContent";
import { useYammerTileNavigation } from "client/Features/Workspace/Tiles/Yammer/YammerTileContent";
import { useZaaksysteemTileNavigation } from "client/Features/Workspace/Tiles/Zaaksysteem/ZaaksysteemTileContent";
import { useZenyaDocTileNavigation } from "client/Features/Workspace/Tiles/ZenyaDoc/ZenyaDocTileContent";
import { useZermeloTileNavigation } from "client/Features/Workspace/Tiles/Zermelo/ZermeloTileContent";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { ConnectWiseLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/ConnectWise/provider";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";

export function useFrequentlyUsedTileNavigation(
	userTimeZone: TimeZone
): [
	element: ReactElement | null,
	onInvoke: (item: FrequentlyUsedTileModel) => void
] {
	const [activeInvocations, setActiveInvocations] = useState<readonly ActiveInvocation[]>([]);

	const markInvocationAsComplete = useCallback(
		(id: Guid): void => {
			setActiveInvocations(
				(x) => x.filter((x) => x.id !== id)
			);
		},
		[]
	);

	const onInvoke = useCallback(
		(item: FrequentlyUsedTileModel): void => {
			const renderStateIsh: RenderStateIsh<AppType> = buildRenderStateIsh(item);
			const id: Guid = newGuid();

			setActiveInvocations(
				(x) => [...x, { id, renderStateIsh }]
			);
		},
		[]
	);

	const element: ReactElement | null = activeInvocations.length > 0
		? (
			<>
				{activeInvocations.map((x) => (
					<TileNavigationInvocation
						key={x.id}
						renderStateIsh={x.renderStateIsh}
						userTimeZone={userTimeZone}
						onDone={() => markInvocationAsComplete(x.id)}
					/>
				))}
			</>
		)
		: null;

	return [element, onInvoke];
};

type RenderStateIsh<TKind extends AppType> = Pick<TileRenderState<TKind>, "data" | "interactionState" | "dimension">;

type ActiveInvocation = { id: Guid; renderStateIsh: RenderStateIsh<AppType>; };

function buildRenderStateIsh(
	item: FrequentlyUsedTileModel
): RenderStateIsh<AppType> {
	const tileData: TileData = convertTile(
		item.tile,
		item.conditionalAccess
			? {
				reason: item.conditionalAccess
					.reason,
				notificationMessage: item.conditionalAccess
					.reason,
				hideTile: item.conditionalAccess
					.shouldHide
			}
			: undefined,
		item.maintenanceWindow
			? {
				notificationMessage: item.maintenanceWindow
					.notificationMessage,
				endsOn: parseDtoDate(
					item.maintenanceWindow
						.endsOn
				)
			}
			: undefined
	);

	const renderIsh: RenderStateIsh<AppType> = {
		data: tileData,
		dimension: {
			kind: "normal",
			dimension: { columnSpan: 1, rowSpan: 1 }
		},
		interactionState: InteractionState.Default
	};

	return renderIsh;
}

type TileNavigationInvocationProps<TKind extends AppType> = {
	renderStateIsh: Pick<TileRenderState<TKind>, "data" | "interactionState" | "dimension">;
	userTimeZone: TimeZone;
	onDone: () => void;
};

const TileNavigationInvocation = (props: TileNavigationInvocationProps<AppType>): ReactElement => {
	const {
		renderStateIsh,
		userTimeZone,
		onDone
	} = props;

	// Unfortunately we cannot track the TKind generic here, so we widen to any
	const Component: TileNavigationInvocationComponent<any> = kindToImplementationMap[renderStateIsh.data.type];

	return (
		<Component
			renderStateIsh={renderStateIsh}
			userTimeZone={userTimeZone}
			onDone={onDone}
		/>
	);
};
TileNavigationInvocation.displayName = "TileNavigationInvocation";

const fakeConnectWiseProvider: ConnectWiseLiveInformationProvider = new ConnectWiseLiveInformationProvider({
	intervalInSeconds: Number.MAX_SAFE_INTEGER,
	// In order to skip the "Incomplete configuration check"
	isConnectWiseConfigured: true,
	customerPortalUrl: null!
});

type TileNavigationInvocationComponent<TKind extends AppType> = (
	props: TileNavigationInvocationProps<TKind>
) => ReactElement | null;

const kindToImplementationMap: { [k in AppType]: TileNavigationInvocationComponent<k>; } = {
	[AppType.Unknown]: buildComponent(useUnknownTileNavigation),
	[AppType.Url]: buildComponent(useShortcutTileNavigation),
	[AppType.Email]: buildComponent(useEmailTileNavigation),
	[AppType.Documents]: buildComponent(useDocumentsTileNavigation),
	[AppType.Office365Word]: buildComponent<AppType.Office365Word>(useOfficeTileNavigation),
	[AppType.Office365Excel]: buildComponent<AppType.Office365Excel>(useOfficeTileNavigation),
	[AppType.Office365PowerPoint]: buildComponent<AppType.Office365PowerPoint>(useOfficeTileNavigation),
	[AppType.Office365OneNote]: buildComponent<AppType.Office365OneNote>(useOfficeTileNavigation),
	[AppType.Office365LyncMeeting]: buildComponent(useSkypeTileNavigation),
	[AppType.Office365PeopleScreen]: buildComponent(usePeopleTileNavigation),
	[AppType.Office365Calendar]: buildComponent(useCalendarTileNavigation),
	[AppType.CitrixXenApp]: buildComponent(useCitrixXenAppTileNavigation),
	[AppType.Rss]: buildComponent(() => useNotSupportedTileNavigation()),
	[AppType.AzureApp]: buildComponent(useAzureAppTileNavigation),
	[AppType.ClientlessRdp]: buildComponent(useClientlessRdpTileNavigation),
	[AppType.PowerBi]: buildComponent((x, isEditable) => usePowerBiTileNavigation(x, isEditable)),
	[AppType.Yammer]: buildComponent(useYammerTileNavigation),
	[AppType.Embed]: buildComponent(useEmbedAppTileNavigation),
	[AppType.ToDo]: buildComponent(useToDoTileNavigation),
	[AppType.Google]: buildComponent(useGoogleTileNavigation),
	[AppType.TopDesk]: buildComponent(useTopDeskTileNavigation),
	[AppType.ConnectWise]: buildComponent((x, isEditable) => useConnectWiseTileNavigation(x, isEditable, fakeConnectWiseProvider)),
	[AppType.Teams]: buildComponent(useTeamsTileNavigation),
	[AppType.SharePoint]: buildComponent(useSharePointTileNavigation),
	[AppType.AddressBook]: buildComponent(useAddressBookTileNavigation),
	[AppType.HowTo]: buildComponent(() => useNotSupportedTileNavigation()),
	[AppType.Hub]: buildComponent(useHubTileNavigation),
	[AppType.LocalApp]: buildComponent(useLocalAppTileNavigation),
	[AppType.WindowsVirtualDesktop]: buildComponent(useWindowsVirtualDesktopTileNavigation),
	[AppType.VilansKick]: buildComponent(useVilansKickTileNavigation),
	[AppType.Birthday]: buildComponent(() => useNotSupportedTileNavigation()),
	[AppType.Autotask]: buildComponent(useAutotaskTileNavigation),
	[AppType.YouTube]: buildComponent(() => useNotSupportedTileNavigation()),
	[AppType.NedapOns]: buildComponent(useNedapOnsTileNavigation),
	[AppType.Afas]: buildComponent(useAfasTileNavigation),
	[AppType.Zaaksysteem]: buildComponent(useZaaksysteemTileNavigation),
	[AppType.Zermelo]: buildComponent(useZermeloTileNavigation),
	[AppType.ZenyaDoc]: buildComponent(useZenyaDocTileNavigation),
	[AppType.Liquit]: buildComponent(useLiquitTileNavigation),
	[AppType.VmwareHorizonApp]: buildComponent(useVmwareHorizonAppTileNavigation),
	[AppType.Magister]: buildComponent(useMagisterTileNavigation),
	[AppType.Integration]: buildComponent(() => useNotSupportedTileNavigation())
};

type TileNavigationHookResult = [
	navigationElement: ReactElement | null,
	navigateHandler: () => Promise<void> | void
];

function buildComponent<TKind extends AppType>(
	hookFactory: (
		renderStateIsh: Pick<TileRenderState<TKind>, "data" | "interactionState" | "dimension">,
		onEdit: (() => void) | undefined,
		userTimeZone: TimeZone
	) => TileNavigationHookResult
): ((props: TileNavigationInvocationProps<TKind>) => ReactElement | null) {

	const HocTileNavigationInvocation = (props: TileNavigationInvocationProps<TKind>): ReactElement | null => {
		const {
			renderStateIsh,
			userTimeZone,
			onDone
		} = props;

		return useTileNavigationInvocation(
			hookFactory(
				renderStateIsh,
				undefined,
				userTimeZone
			),
			onDone
		);
	};
	HocTileNavigationInvocation.displayName = "HocTileNavigationInvocation";
	return HocTileNavigationInvocation;
}

function useTileNavigationInvocation(
	navigationHookResult: TileNavigationHookResult,
	onDone: () => void
): ReactElement | null {
	const [navigationElement, navigateHandler] = navigationHookResult;

	const stableNavigateHandler: () => Promise<void> | void = useStableImperativeCallback(navigateHandler);
	const stableOnDone: () => void = useStableImperativeCallback(onDone);

	useEffect(
		(): void => {
			(async () => {
				await stableNavigateHandler();
				stableOnDone();
			})();
		},
		[stableNavigateHandler, stableOnDone]
	);

	return navigationElement;
}

function useNotSupportedTileNavigation(): [element: ReactElement | null, () => void] {
	const alertManager: AlertManager = useAlerts();

	return [
		null,
		useCallback(
			(): void => {
				alertManager.showError(localize("_NotSupported"));
			},
			[alertManager]
		)
	];
}
